import React, { useRef, useEffect, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { useFilters } from '../../contexts/FiltersContext';
import { propertyService } from '../../services/propertyService';
import PropertyPopup from './PropertyPopup';
import { PropertyFeature } from '../../types/property';

const token = process.env.REACT_APP_MAPBOX_TOKEN || '';
console.log('Mapbox token from env:', token ? `${token.substring(0, 10)}...` : 'MISSING');
mapboxgl.accessToken = token;

const MapContainer: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<PropertyFeature | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { filters, assumptions } = useFilters();

  // Keep a stable ref so event handlers always call the latest version
  const loadPropertiesRef = useRef<(forceSync?: boolean) => Promise<void>>(async () => {});
  const retryRef = useRef<NodeJS.Timeout | null>(null);

  const loadProperties = useCallback(async (forceSync = false) => {
    if (!map.current) return;

    // Cancel any pending retry — a fresh load supersedes it
    if (retryRef.current) {
      clearTimeout(retryRef.current);
      retryRef.current = null;
    }

    setLoading(true);
    try {
      const bounds = map.current.getBounds();
      if (!bounds) return;
      const bbox = {
        min_lat: bounds.getSouth(),
        max_lat: bounds.getNorth(),
        min_lon: bounds.getWest(),
        max_lon: bounds.getEast()
      };
      
      // Split filters: send only property-based filters to backend
      // Backend will calculate metrics with assumptions, then we filter client-side
      const backendFilters = {
        status: filters.status,
        property_type: filters.property_type,
        bedrooms_min: filters.bedrooms_min,
        baths_min: filters.baths_min,
        sqft_min: filters.sqft_min,
        sqft_max: filters.sqft_max,
        price_min: filters.price_min,
        price_max: filters.price_max,
      };
      
      // Backend calculates with assumptions, we filter by investment minimums client-side
      const data = await propertyService.getPropertiesInViewport(bbox, backendFilters, assumptions);
      
      // Client-side filter by investment minimums (based on backend-calculated metrics with assumptions)
      let filteredData = { ...data };
      if (filters.cash_flow_min !== undefined) {
        filteredData.features = data.features.filter((f: any) => 
          Math.round(f.properties.monthly_cash_flow || 0) >= Math.round(filters.cash_flow_min!)
        );
      }
      if (filters.cap_rate_min !== undefined) {
        filteredData.features = filteredData.features.filter((f: any) => 
          (f.properties.cap_rate || 0) >= filters.cap_rate_min!
        );
      }
      if (filters.coc_return_min !== undefined) {
        filteredData.features = filteredData.features.filter((f: any) => 
          (f.properties.cash_on_cash_return || 0) >= filters.coc_return_min!
        );
      }
      if (filters.roi_min !== undefined) {
        filteredData.features = filteredData.features.filter((f: any) => 
          (f.properties.roi || 0) >= filters.roi_min!
        );
      }
      
      const source = map.current.getSource('properties') as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData(filteredData as any);
      }
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, assumptions]);

  // Keep ref in sync — no effect deps on loadProperties for map init
  useEffect(() => {
    loadPropertiesRef.current = loadProperties;
  }, [loadProperties]);

  // Initialize map once — no dep on loadProperties so it never destroys/re-inits on filter change
  useEffect(() => {
    if (map.current) return;

    if (!token) {
      setError('Mapbox token is missing');
      console.error('REACT_APP_MAPBOX_TOKEN is not set');
      return;
    }

    try {
      console.log('Initializing Mapbox map...');
      const mapInstance = new mapboxgl.Map({
      container: mapContainer.current!,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [
        Number(process.env.REACT_APP_MAP_CENTER_LON) || -81.6944,
        Number(process.env.REACT_APP_MAP_CENTER_LAT) || 41.4993
      ],
      zoom: Number(process.env.REACT_APP_MAP_ZOOM) || 11
    });

    mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.current = mapInstance;

    // Load properties when map is loaded
    mapInstance.on('load', () => {
      // Build a pill-shaped SDF image for use as icon background
      const pillW = 80, pillH = 32, pillR = pillH / 2;
      const pillCanvas = document.createElement('canvas');
      pillCanvas.width = pillW; pillCanvas.height = pillH;
      const pillCtx = pillCanvas.getContext('2d')!;
      pillCtx.fillStyle = 'white';
      pillCtx.beginPath();
      pillCtx.moveTo(pillR, 0);
      pillCtx.lineTo(pillW - pillR, 0);
      pillCtx.arcTo(pillW, 0, pillW, pillR, pillR);
      pillCtx.lineTo(pillW, pillH - pillR);
      pillCtx.arcTo(pillW, pillH, pillW - pillR, pillH, pillR);
      pillCtx.lineTo(pillR, pillH);
      pillCtx.arcTo(0, pillH, 0, pillH - pillR, pillR);
      pillCtx.lineTo(0, pillR);
      pillCtx.arcTo(0, 0, pillR, 0, pillR);
      pillCtx.closePath();
      pillCtx.fill();
      const pillData = pillCtx.getImageData(0, 0, pillW, pillH);
      mapInstance.addImage('pill-bg', { width: pillW, height: pillH, data: pillData.data as unknown as Uint8Array }, { sdf: true });

      // Add source — no clustering, one dot per property
      mapInstance.addSource('properties', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      // Cash-flow color ramp
      const cfColorExpr: any = [
        'step', ['coalesce', ['get', 'monthly_cash_flow'], 0],
        '#ef4444',   // < 0    → red
        0,   '#fb923c',  // 0–100  → orange
        100, '#86efac',  // 100–300 → light green
        300, '#4ade80',  // 300–500 → medium green
        500, '#22c55e'   // 500+   → dark green
      ];

      // Circle — zoomed out only, fades away as price pill takes over
      mapInstance.addLayer({
        id: 'property-circle',
        type: 'circle',
        source: 'properties',
        paint: {
          'circle-color': cfColorExpr,
          'circle-radius': [
            'interpolate', ['linear'], ['zoom'],
            8,  5,
            13, 10
          ],
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': ['step', ['zoom'], 0.9, 12, 0],
          'circle-stroke-opacity': ['step', ['zoom'], 1, 12, 0]
        }
      });

      // Price pill — zoomed in: colored bubble with price inside
      mapInstance.addLayer({
        id: 'property-label',
        type: 'symbol',
        source: 'properties',
        minzoom: 11,
        layout: {
          'icon-image': 'pill-bg',
          'icon-text-fit': 'both',
          'icon-text-fit-padding': [4, 8, 4, 8],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'text-field': [
            'case',
            ['>=', ['coalesce', ['get', 'list_price'], 0], 1000000],
            ['concat',
              ['number-format', ['/', ['coalesce', ['get', 'list_price'], 0], 1000000], { 'min-fraction-digits': 1, 'max-fraction-digits': 1 }],
              'M'
            ],
            ['concat',
              ['number-format', ['/', ['coalesce', ['get', 'list_price'], 0], 1000], { 'min-fraction-digits': 0, 'max-fraction-digits': 0 }],
              'k'
            ]
          ],
          'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 12, 11, 15, 14],
          'text-anchor': 'center',
          'text-offset': [0, 0],
          'text-allow-overlap': true,
          'text-ignore-placement': true
        },
        paint: {
          'icon-color': cfColorExpr,
          'icon-opacity': ['step', ['zoom'], 0, 12, 1],
          'text-color': '#ffffff',
          'text-opacity': ['step', ['zoom'], 0, 12, 1]
        }
      });

      // Highlight ring — selected property circle (zoomed out)
      mapInstance.addLayer({
        id: 'property-selected-circle',
        type: 'circle',
        source: 'properties',
        filter: ['==', ['get', 'id'], -1],
        paint: {
          'circle-color': '#ffffff',
          'circle-radius': [
            'interpolate', ['linear'], ['zoom'],
            8, 9,
            13, 16
          ],
          'circle-stroke-width': 3,
          'circle-stroke-color': '#1d4ed8',
          'circle-opacity': ['step', ['zoom'], 1, 12, 0],
          'circle-stroke-opacity': ['step', ['zoom'], 1, 12, 0]
        }
      });

      // Highlight outline — selected property pill (zoomed in)
      mapInstance.addLayer({
        id: 'property-selected-label',
        type: 'symbol',
        source: 'properties',
        filter: ['==', ['get', 'id'], -1],
        minzoom: 11,
        layout: {
          'icon-image': 'pill-bg',
          'icon-text-fit': 'both',
          'icon-text-fit-padding': [6, 11, 6, 11],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'text-field': [
            'case',
            ['>=', ['coalesce', ['get', 'list_price'], 0], 1000000],
            ['concat',
              ['number-format', ['/', ['coalesce', ['get', 'list_price'], 0], 1000000], { 'min-fraction-digits': 1, 'max-fraction-digits': 1 }],
              'M'
            ],
            ['concat',
              ['number-format', ['/', ['coalesce', ['get', 'list_price'], 0], 1000], { 'min-fraction-digits': 0, 'max-fraction-digits': 0 }],
              'k'
            ]
          ],
          'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 12, 11, 15, 14],
          'text-anchor': 'center',
          'text-offset': [0, 0],
          'text-allow-overlap': true,
          'text-ignore-placement': true
        },
        paint: {
          'icon-color': '#1d4ed8',
          'icon-opacity': ['step', ['zoom'], 0, 12, 1],
          'text-color': '#ffffff',
          'text-opacity': ['step', ['zoom'], 0, 12, 1]
        }
      });

      // Click handler for individual properties
      mapInstance.on('click', 'property-circle', (e) => {
        if (e.features && e.features[0]) {
          setSelectedProperty(e.features[0] as unknown as PropertyFeature);
        }
      });

      mapInstance.on('click', 'property-label', (e) => {
        if (e.features && e.features[0]) {
          setSelectedProperty(e.features[0] as unknown as PropertyFeature);
        }
      });

      // Change cursor on hover
      mapInstance.on('mouseenter', 'property-circle', () => {
        mapInstance.getCanvas().style.cursor = 'pointer';
      });
      mapInstance.on('mouseleave', 'property-circle', () => {
        mapInstance.getCanvas().style.cursor = '';
      });
      mapInstance.on('mouseenter', 'property-label', () => {
        mapInstance.getCanvas().style.cursor = 'pointer';
      });
      mapInstance.on('mouseleave', 'property-label', () => {
        mapInstance.getCanvas().style.cursor = '';
      });

      loadPropertiesRef.current();
    });

    // Reload on pan, using ref so filters are always current
    let moveTimeout: NodeJS.Timeout;
    mapInstance.on('moveend', () => {
      clearTimeout(moveTimeout);
      moveTimeout = setTimeout(() => {
        loadPropertiesRef.current();
      }, 600);
    });

    console.log('Map initialized successfully');
    
    return () => {
      mapInstance.remove();
    };
  } catch (err) {
    console.error('Error initializing map:', err);
    setError(err instanceof Error ? err.message : 'Failed to initialize map');
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []); // empty deps — map init runs exactly once

  // Update highlight layers when selected property changes
  useEffect(() => {
    if (!map.current?.isStyleLoaded()) return;
    const id = selectedProperty?.properties?.id ?? -1;
    const filter: any = ['==', ['get', 'id'], id];
    map.current.setFilter('property-selected-circle', filter);
    map.current.setFilter('property-selected-label', filter);
  }, [selectedProperty]);

  // Reload when filters or assumptions change
  useEffect(() => {
    if (map.current?.isStyleLoaded()) {
      loadProperties();
    }
  }, [filters, assumptions, loadProperties]);

  return (
    <div className="relative w-full h-full">
      {error ? (
        <div className="flex items-center justify-center h-full bg-gray-50">
          <div className="text-center p-8">
            <div className="text-red-600 text-lg font-semibold mb-2">Map Error</div>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              Check browser console (F12) for details
            </p>
          </div>
        </div>
      ) : (
        <>
          <div ref={mapContainer} className="map-container" />

          {selectedProperty && (
            <PropertyPopup
              property={selectedProperty}
              onClose={() => setSelectedProperty(null)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default MapContainer;
